export type NormalizedTradeForDetails = {
  symbol: string;
  side: 'LONG' | 'SHORT' | null;
  assetClass: string | null;
  quantity: number | null;
  tradedSize: number | null;
  maxPositionSize: number | null;
  entryPrice: number | null;
  exitPrice: number | null;
  grossPnl: number | null;
  netPnl: number | null;
  realizedPnl: number | null;
  fees: number | null;
  initialRisk: number | null;
  rMultiple: number | null;
  mae: number | null;
  mfe: number | null;
  maeGross: number | null;
  maeNet: number | null;
  mfeGross: number | null;
  mfeNet: number | null;
  priceCaptured: number | null;
};

function toFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function firstFinite(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed != null) return parsed;
  }
  return null;
}

function firstPositive(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed != null && parsed > 0) return parsed;
  }
  return null;
}

function normalizeSide(raw: unknown, quantity?: number | null): 'LONG' | 'SHORT' | null {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'long' || value === 'buy' || value === 'b' || value === '+') return 'LONG';
  if (value === 'short' || value === 'sell' || value === 's' || value === '-') return 'SHORT';
  if (value.includes('long') || value.includes('buy')) return 'LONG';
  if (value.includes('short') || value.includes('sell')) return 'SHORT';
  if (quantity != null && quantity !== 0) return quantity < 0 ? 'SHORT' : 'LONG';
  return null;
}

function normalizeAssetClass(raw: unknown): string | null {
  const value = String(raw ?? '').trim().toUpperCase();
  if (!value) return null;
  if (value === 'STK' || value === 'STOCK' || value === 'STOCKS' || value === 'EQUITY' || value === 'EQUITIES') return 'STK';
  if (value.includes('FUT')) return 'FUT';
  if (value === 'CASH' || value === 'FOREX' || value === 'FX') return 'CASH';
  if (value === 'CRYPTO') return 'CRYPTO';
  return value;
}

function normalizeMae(value: number | null): number | null {
  if (value == null) return null;
  if (value === 0) return 0;
  return value > 0 ? -value : value;
}

function normalizeMfe(value: number | null): number | null {
  if (value == null) return null;
  if (value === 0) return 0;
  return value < 0 ? Math.abs(value) : value;
}

export function normalizeTradeForDetails(trade: Record<string, any> | null | undefined): NormalizedTradeForDetails {
  const quantityRaw = firstFinite(
    trade?.quantity,
    trade?.shares,
    trade?.qty,
    trade?.size,
    trade?.volume,
    trade?.closedQty,
    trade?.rowResolvedQty,
  );
  const quantity = quantityRaw == null ? null : Math.abs(quantityRaw);
  const tradedSize = firstPositive(trade?.tradedSize, trade?.totalSharesTraded, trade?.totalQuantity, quantity);
  const maxPositionSize = firstPositive(
    trade?.maxPositionSize,
    trade?.maxOpenPosition,
    trade?.peakPositionSize,
    trade?.peakOpenPosition,
    trade?.maxPosition,
    quantity,
  );
  const netPnl = firstFinite(
    trade?.netPnl,
    trade?.net_pnl,
    trade?.realizedPnl,
    trade?.realizedNetPnl,
    trade?.realized_net,
    trade?.calculatedPnl,
    trade?.pnl,
    trade?.brokerReportedPnl,
  );
  const explicitFees = firstFinite(
    trade?.commissionAndFees,
    trade?.commissionsAndFees,
    trade?.feesAndCommissions,
    trade?.totalFees,
  );
  const feeParts = [trade?.fees, trade?.fee, trade?.commission, trade?.commissions]
    .map(toFiniteNumber)
    .filter((value): value is number => value != null);
  const fees = explicitFees != null
    ? Math.abs(explicitFees)
    : (feeParts.length > 0 ? Math.abs(feeParts.reduce((sum, value) => sum + value, 0)) : null);
  const grossPnl = firstFinite(
    trade?.grossPnl,
    trade?.gross_pnl,
    trade?.realizedGrossPnl,
    trade?.realized_gross,
    trade?.rowRealizedGross,
  ) ?? (netPnl != null && fees != null ? netPnl + fees : netPnl);
  const realizedPnl = netPnl;
  const initialRisk = firstPositive(trade?.initialRisk, trade?.initialRiskAmount, trade?.riskAmount, trade?.risk);
  const rMultiple = firstFinite(trade?.pnlR, trade?.rMultiple, trade?.r) ?? (
    initialRisk != null && realizedPnl != null ? realizedPnl / initialRisk : null
  );
  const entryPrice = firstPositive(trade?.entryPrice, trade?.avgEntryPrice, trade?.avgEntry, trade?.price);
  const exitPrice = firstPositive(trade?.exitPrice, trade?.avgExitPrice, trade?.avgExit);
  const side = normalizeSide(trade?.side ?? trade?.direction ?? trade?.position, quantityRaw);
  const priceCaptured = entryPrice != null && exitPrice != null && side != null
    ? (side === 'SHORT' ? entryPrice - exitPrice : exitPrice - entryPrice)
    : null;
  const maeGross = normalizeMae(firstFinite(
    trade?.maeGross,
    trade?.mae_gross,
    trade?.mae,
    trade?.positionMAE,
    trade?.maxAdverseExcursion,
    trade?.adverseExcursion,
    trade?.deterministicMae,
  ));
  const mfeGross = normalizeMfe(firstFinite(
    trade?.mfeGross,
    trade?.mfe_gross,
    trade?.mfe,
    trade?.positionMFE,
    trade?.maxFavorableExcursion,
    trade?.favorableExcursion,
    trade?.deterministicMfe,
  ));
  const maeNet = firstFinite(trade?.maeNet, trade?.mae_net)
    ?? (maeGross != null && fees != null ? maeGross - fees : maeGross);
  const mfeNet = firstFinite(trade?.mfeNet, trade?.mfe_net)
    ?? (mfeGross != null && fees != null ? mfeGross - fees : mfeGross);
  const mae = maeGross;
  const mfe = mfeGross;

  return {
    symbol: String(trade?.symbol ?? '').trim(),
    side,
    assetClass: normalizeAssetClass(trade?.assetClass ?? trade?.market ?? trade?.marketType ?? trade?.instrumentType),
    quantity,
    tradedSize,
    maxPositionSize,
    entryPrice,
    exitPrice,
    grossPnl,
    netPnl,
    realizedPnl,
    fees,
    initialRisk,
    rMultiple,
    mae,
    mfe,
    maeGross,
    maeNet,
    mfeGross,
    mfeNet,
    priceCaptured,
  };
}
